<template>
  <div v-if="modelValue === undefined">
    <div class="product-container">
      <p class="notification-subtitle paddingBottom no-margin">Choose Group / Agent</p>
      <div class="selectProductSearch">
        <VSelect
          v-model="selectedRole"
          aria-label="chooseGroup"
          appearance="ghost"
          class="vivid-input"
          fixed-dropdown
        >
          <VOption v-for="thisRole in allRoles" :key="thisRole.id" :value="thisRole.name" :text="thisRole.name" />
        </VSelect>
      </div>
      <template v-if="selectedRole && selectedRoleId">
        <div v-if="agentsToShow.length > 0" class="productListItems">
          <div class="agent-list">
            <div class="agent-item" @click="onSelect(selectedRoleId)">
              <img src="@/resources/images/page-inbox-human-support.svg" class="productImg" alt="Assign to Group" />
              <div class="productDesc">
                <p class="title no-margin">Assign to Group</p>
              </div>
              <span class="radio-indicator" :class="{ checked: modelValue === selectedRoleId }"></span>
            </div>
            <div
              v-for="thisAgent in agentsToShow"
              :key="thisAgent.id"
              class="agent-item"
              @click="onSelect(thisAgent.id)"
            >
              <img :src="thisAgent.picture || defaultPersonImg" class="productImg" :alt="thisAgent.name" />
              <div class="productDesc">
                <p class="title no-margin">{{ thisAgent.name }}</p>
              </div>
              <span class="radio-indicator" :class="{ checked: modelValue === thisAgent.id }"></span>
            </div>
          </div>
        </div>
        <div v-else class="text-center info-message">
          <img src="@/resources/images/empty_box.svg" class="emptyProducts" alt="No agents or groups found" />
          <h5>No Agents/Groups found!</h5>
        </div>
      </template>
    </div>
  </div>
  <div v-else>
    <div class="selected-agent-card">
      <img
        v-if="resolvedPayloadItem"
        :src="resolvedPayloadItem.picture || defaultPersonImg"
        class="agent-avatar"
        :alt="resolvedPayloadItem.name"
      />
      <div class="agent-info">
        <h2 class="title custPadding">{{ resolvedPayloadItem?.name }}</h2>
      </div>
      <VIcon name="delete-line" connotation="alert" class="trashIcon cursorPointer" @click="onClear"></VIcon>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import defaultPersonImgUrl from '@/resources/images/single_person.svg';

export interface Role {
  id: string
  name: string
  picture?: string
}

export interface Agent {
  id: string
  name: string
  role: string
  picture?: string
}

const props = defineProps<{
  modelValue: string | undefined
  allRoles: Role[]
  allAgents: Agent[]
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | undefined]
  'clear': []
}>();

const defaultPersonImg = defaultPersonImgUrl;

const selectedRole = ref<string | undefined>(undefined);

const selectedRoleId = computed(() => props.allRoles.find(r => r.name === selectedRole.value)?.id);

const agentsToShow = computed(() =>
  selectedRole.value ? props.allAgents.filter(a => a.role === selectedRole.value) : []
);

const resolvedPayloadItem = computed<Role | Agent | undefined>(() => {
  if (!props.modelValue) return undefined;
  return (props.allRoles.find(r => r.id == props.modelValue) as Role | Agent | undefined)
    ?? props.allAgents.find(a => a.id == props.modelValue);
});

function onSelect(id: string) {
  emit('update:modelValue', id);
}

function onClear() {
  emit('update:modelValue', undefined);
  emit('clear');
}
</script>

<style scoped>
.product-container {
  margin-top: 20px;
  margin-bottom: 20px;
}
.selectProductSearch {
  margin-bottom: 10px;
  background: var(--background-primary);
  border-radius: 5px;
}
.agent-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.agent-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--background-primary);
  border-radius: 5px;
  padding: 10px 16px;
  cursor: pointer;
  min-height: 60px;
}
.agent-item:hover {
  background: var(--purple-opac);
}
.productListItems {
  overflow-y: auto;
  max-height: 62vh;
}
.productImg {
  width: 40px;
  height: 40px;
  border-radius: 5px;
  flex-shrink: 0;
}
.productDesc {
  flex: 1;
}
.radio-indicator {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--gray-04);
  flex-shrink: 0;
  position: relative;
}
.radio-indicator.checked {
  border-color: var(--purple);
}
.radio-indicator.checked::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--purple);
}
.selected-agent-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--background-primary);
  border-radius: 5px;
  padding: 12px 16px;
  margin-top: 20px;
}
.agent-avatar {
  width: 40px;
  height: 40px;
  border-radius: 5px;
  flex-shrink: 0;
}
.agent-info {
  flex: 1;
}
.no-margin {
  margin: 0;
}
.paddingBottom {
  padding-bottom: 10px;
}
.custPadding {
  padding-bottom: 5px;
}
</style>
